import { Inject, Injectable, OnDestroy } from '@angular/core';
import { getUniqueMedia } from '@x/common/ads/ad-media.config';
import { AD_CONFIG, IAdConfig, IAdGlobalFilters, TAdPlacement } from '@x/common/ads/ad.config';
import { TShopAd } from '@x/ecommerce/shop-client';
import { BehaviorSubject, Observable, Subscription, withLatestFrom } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class AdService implements OnDestroy {
  private subscription = new Subscription();
  private adPool$ = new BehaviorSubject<TShopAd[]>([]);
  private taxonPathFromFilters: string;

  // cache
  private selectedCache: Map<number, TShopAd> = new Map();

  constructor(@Inject(AD_CONFIG) private adConfig: IAdConfig) {
    if (!this.adConfig) throw new Error('AD_CONFIG is not provided');

    this.subscription.add(
      this.adConfig.globalFilters$
        .pipe(
          withLatestFrom(this.adConfig.ads$),
          map(([filters, ads]) => {
            /*reset cache*/
            this.selectedCache = new Map();
            this.taxonPathFromFilters = filters.taxonPath;

            return this.getAdPool(ads, filters);
          }),
        )
        .subscribe((pool) => this.adPool$.next(pool)),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ad$(placement: TAdPlacement, taxonPathInContext?: string): Observable<TShopAd | null> {
    return this.adPool$.pipe(
      map((pool) => {
        if (pool.length === 0) return null;

        const taxonPath = taxonPathInContext ?? this.taxonPathFromFilters;

        const filterAds = pool.reduce((acc, ad) => {
          const isNotSelected = !this.selectedCache.has(ad.id);

          const hasTargetTaxon = ad.targetTaxons.some((t) => {
            return ad.targetChildrenTaxons ? taxonPath.startsWith(t.path) : taxonPath === t.path;
          });

          const hasValidMedia = ad.media.some((m) => m.placement === placement);

          if (isNotSelected && hasTargetTaxon && hasValidMedia) {
            const uniqueMedia = getUniqueMedia(ad.media, placement);
            acc.push({ ...ad, media: uniqueMedia });
          }

          return acc;
        }, [] as TShopAd[]);

        if (!filterAds.length) return null;

        const weightedAd = this.getWeightedRandomAd(filterAds);
        if (!weightedAd) return null;

        this.cacheAd(weightedAd);

        return weightedAd;
      }),
    );
  }

  cacheAd(shopAd: TShopAd) {
    this.selectedCache.set(shopAd.id, shopAd);
  }

  releaseAd(shopAd: TShopAd) {
    this.selectedCache.delete(shopAd.id);
  }

  private getWeightedRandomAd(ads: TShopAd[]): TShopAd | null {
    const totalWeight = ads.reduce((sum, item) => sum + item.weight, 0);
    if (totalWeight <= 0) return null;

    let random = Math.random() * totalWeight;
    for (const item of ads) {
      if (random < item.weight) return item;
      random -= item.weight;
    }

    return null;
  }

  private getAdPool(ads: TShopAd[], filters: IAdGlobalFilters): TShopAd[] {
    const { taxonPath, regionId, loggedIn } = filters;

    //taxons
    let adPool = ads.filter((ad) => {
      const adTaxonPaths = ad.targetTaxons.map((t) => t.path);

      return adTaxonPaths.length === 0 || adTaxonPaths.some((path) => path.startsWith(taxonPath));
    });

    if (!adPool.length) return [];

    // regions
    adPool = adPool.filter(
      (ad) =>
        ad.targetRegions.length === 0 ||
        (regionId && ad.targetRegions.map((r) => r.id).includes(regionId)),
    );

    //auth
    adPool = adPool.filter(
      (ad) =>
        ad.targetAuthorizationType === 'ANY' ||
        ad.targetAuthorizationType === (loggedIn ? 'AUTHENTICATED' : 'ANONYMOUS'),
    );

    return adPool;
  }
}
